import React from 'react';
import {
    type FieldPath,
    type FieldValues,
    type PathValue,
} from 'react-hook-form';
import type {SetRequired} from 'type-fest';
import {Radio, type RadioProps} from '@mui/material';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {splitEventHandler} from '@netx/core-next/utils';
import {combineRefs} from '@netx/core-next/utils/react';
import {
    ReactHookFormControl,
    type WithReactHookFormControlFieldProps,
} from '../ReactHookFormControl';

export interface ReactHookFormPlainRadioFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
        SetRequired<RadioProps, 'name'>,
        TFieldValues,
        TName
    > {
    value: number | string;
}

export const ReactHookFormPlainRadioFieldPrimitive = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
    {
        control,
        ControllerProps,
        ...props
    }: ReactHookFormPlainRadioFieldProps<TFieldValues, TName>,
    ref: React.ForwardedRef<HTMLButtonElement>,
) => {
    return (
        <ReactHookFormControl<TFieldValues, TName>
            {...ControllerProps}
            control={control}
            name={props.name as TName}
            render={({field, formState}) => (
                <Radio
                    {...props}
                    {...field}
                    value={props.value}
                    checked={
                        props.checked != null ? props.checked : field.value === props.value
                    }
                    disabled={field.disabled || props.disabled}
                    ref={ref}
                    inputRef={combineRefs(props.inputRef, field.ref)}
                    onChange={useConstCallback(
                        splitEventHandler(props.onChange, (event, checked) => {
                            field.onChange(props.value);
                        }),
                    )}
                />
            )}
            rules={{
                required: props.required,
                validate: async (value) => {
                    return props.required && !value ? i18n.t('i18n.required') : true;
                },
                ...ControllerProps?.rules,
            }}
        />
    );
};

/**
 * `react-hook-form` controlled `Checkbox` component
 */
export const ReactHookFormPlainRadioField = React.forwardRef(
    ReactHookFormPlainRadioFieldPrimitive,
) as <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
    props: ReactHookFormPlainRadioFieldProps<TFieldValues, TName>,
) => JSX.Element;

export default ReactHookFormPlainRadioField;
